import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import FadeIn from 'react-fade-in';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Page from 'src/components/Page';
import MainLayout from 'src/layouts/MainLayout';
import I18nMenu from 'src/layouts/DashboardLayout/I18nMenu';
import Footer from 'src/components/Footer';
import Loading from 'src/components/Loading';
import useStyles from 'src/views/auth/styles';
import {
  ADD_CARD_TO_CLIENT,
  VALIDATE_CARD_FORM_TOKEN
} from 'src/store/actions/cardFormActions';
import {
  selectCardFormTokenData,
  selectCardFormTokenValidationStatus
} from 'src/store/selectors/cardFormSelectors';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import TokenValidationStatus from 'src/utils/constants/cardForm';
import useOpenPayCardForm from 'src/hooks/useOpenPayCardForm';
import theme from 'src/theme';
// eslint-disable-next-line import/extensions
import validationSchema from './OpenPayCardFormView/schema';
// eslint-disable-next-line import/extensions
import makeInitialState from './OpenPayCardFormView/initialState';

const OpenPayCardFormView = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { token } = useParams();
  const formRef = useRef(null);

  const tokenData = useSelector(selectCardFormTokenData);
  const tokenValidationStatus = useSelector(
    selectCardFormTokenValidationStatus
  );
  const loadingTokenValidation = useSelector(
    isLoadingSelector([VALIDATE_CARD_FORM_TOKEN.action])
  );
  const savingCard = useSelector(
    isLoadingSelector([ADD_CARD_TO_CLIENT.action])
  );

  const clientId = tokenData?.clientId || tokenData?.client_id || null;

  const { loading, error, handleFormSubmit } = useOpenPayCardForm(
    token,
    tokenValidationStatus,
    clientId
  );

  if (
    loadingTokenValidation ||
    tokenValidationStatus === TokenValidationStatus.PENDING
  ) {
    return (
      <MainLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column'
          }}
        >
          <Loading />
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            {t('COMMON.VALIDATING_TOKEN')}
          </Typography>
        </Box>
      </MainLayout>
    );
  }

  if (tokenValidationStatus === TokenValidationStatus.FAILURE) {
    return (
      <MainLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column'
          }}
        >
          <Typography variant="h6" color="error">
            {t('OPENPAY_CARD_FORM.ERRORS.TOKEN_INVALID')}
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {t('COMMON.REDIRECTING')}
          </Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Page
        className={classes.userVerifictionRoot}
        title={t('OPENPAY_CARD_FORM.TITLE')}
      >
        <Box display="flex" flexDirection="column" height="100%">
          <Box sx={{ marginLeft: 'auto' }}>
            <I18nMenu />
          </Box>
          <Container
            className={classes.userVerifictionContainter}
            sx={{
              width: {
                xs: '100%',
                sm: '100%',
                md: '75%'
              }
            }}
          >
            <FadeIn transitionDuration={200}>
              <Box
                sx={{
                  maxWidth: 600,
                  width: '100%',
                  padding: theme.spacing(3),
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 1,
                  boxShadow: theme.shadows[2]
                }}
              >
                <Typography
                  className={classes.title}
                  color="primary"
                  variant="h2"
                  sx={{
                    textAlign: 'center',
                    marginBottom: 3,
                    fontSize: {
                      xs: theme.spacing(3.5),
                      sm: theme.spacing(4),
                      md: theme.spacing(4.25),
                      lg: theme.spacing(5)
                    }
                  }}
                >
                  {t('OPENPAY_CARD_FORM.TITLE')}
                </Typography>

                {error && (
                  <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                    <Alert severity="error">{error}</Alert>
                  </Box>
                )}

                <Formik
                  initialValues={makeInitialState()}
                  validationSchema={validationSchema}
                  onSubmit={handleFormSubmit}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values
                  }) => (
                    <form
                      id="openpay-card-form"
                      ref={formRef}
                      onSubmit={handleSubmit}
                      style={{ width: '100%' }}
                    >
                      <TextField
                        fullWidth
                        margin="normal"
                        label={t('OPENPAY_CARD_FORM.FIELDS.HOLDER_NAME')}
                        variant="outlined"
                        name="holderName"
                        value={values.holderName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.holderName && errors.holderName)}
                        helperText={
                          touched.holderName && errors.holderName
                            ? errors.holderName
                            : ''
                        }
                        inputProps={{
                          'data-openpay-card': 'holder_name'
                        }}
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label={t('OPENPAY_CARD_FORM.FIELDS.CARD_NUMBER')}
                        variant="outlined"
                        name="cardNumber"
                        value={values.cardNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.cardNumber && errors.cardNumber)}
                        helperText={
                          touched.cardNumber && errors.cardNumber
                            ? errors.cardNumber
                            : ''
                        }
                        inputMode="numeric"
                        inputProps={{
                          'data-openpay-card': 'card_number',
                          maxLength: 19
                        }}
                        onKeyPress={e => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onInput={e => {
                          const value = e.target.value
                            .replace(/\s/g, '')
                            .replace(/\D/g, '');
                          if (e.target.value !== value) {
                            e.target.value = value;
                          }
                        }}
                      />

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          marginTop: 1,
                          width: '100%'
                        }}
                      >
                        <TextField
                          fullWidth
                          margin="normal"
                          label={t('OPENPAY_CARD_FORM.FIELDS.EXPIRATION_MONTH')}
                          variant="outlined"
                          name="expirationMonth"
                          value={values.expirationMonth}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.expirationMonth && errors.expirationMonth
                          )}
                          helperText={
                            touched.expirationMonth && errors.expirationMonth
                              ? errors.expirationMonth
                              : ''
                          }
                          placeholder="MM"
                          inputMode="numeric"
                          inputProps={{
                            'data-openpay-card': 'expiration_month',
                            maxLength: 2
                          }}
                        />
                        <TextField
                          fullWidth
                          margin="normal"
                          label={t('OPENPAY_CARD_FORM.FIELDS.EXPIRATION_YEAR')}
                          variant="outlined"
                          name="expirationYear"
                          value={values.expirationYear}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.expirationYear && errors.expirationYear
                          )}
                          helperText={
                            touched.expirationYear && errors.expirationYear
                              ? errors.expirationYear
                              : ''
                          }
                          placeholder="YYYY"
                          inputMode="numeric"
                          inputProps={{
                            'data-openpay-card': 'expiration_year',
                            maxLength: 4
                          }}
                        />
                        <TextField
                          fullWidth
                          margin="normal"
                          label={t('OPENPAY_CARD_FORM.FIELDS.CVV')}
                          variant="outlined"
                          type="password"
                          name="cvv"
                          value={values.cvv}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.cvv && errors.cvv)}
                          helperText={
                            touched.cvv && errors.cvv ? errors.cvv : ''
                          }
                          inputMode="numeric"
                          inputProps={{
                            'data-openpay-card': 'cvv2',
                            maxLength: 4
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          marginTop: 3,
                          textAlign: 'center',
                          width: '100%'
                        }}
                      >
                        <Button
                          className={classes.button}
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={loading || savingCard}
                        >
                          {loading || savingCard
                            ? t('OPENPAY_CARD_FORM.BUTTONS.PROCESSING')
                            : t('OPENPAY_CARD_FORM.BUTTONS.GENERATE_TOKEN')}
                        </Button>
                      </Box>
                    </form>
                  )}
                </Formik>
              </Box>
            </FadeIn>
          </Container>
          <Footer />
        </Box>
      </Page>
    </MainLayout>
  );
};

export default OpenPayCardFormView;
